/**
 * Lightweight pageview beacon.
 * - Generates/persists a session id in localStorage (32 chars, no PII).
 * - Captures UTM params from the URL on first visit; reused for the session.
 * - POSTs to /api/public/growth/pageview on every route change.
 *
 * Fails silently — analytics must never block or crash the app.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'nis2ai_session_id';
const UTM_KEY = 'nis2ai_utm';

function _rand32() {
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

function _sessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = _rand32();
    try { localStorage.setItem(SESSION_KEY, sid); } catch { /* Safari private mode */ }
  }
  return sid;
}

function _capturedUtm() {
  const params = new URLSearchParams(window.location.search);
  const fresh = {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
  };
  const hasAny = Object.values(fresh).some(Boolean);
  if (hasAny) {
    try { localStorage.setItem(UTM_KEY, JSON.stringify(fresh)); } catch { /* noop */ }
    return fresh;
  }
  try {
    const stored = localStorage.getItem(UTM_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function _shouldTrack(path) {
  // Track public + logged-in surfaces; skip auth boilerplate & static.
  if (!path) return false;
  if (path.startsWith('/auth/callback')) return false;
  return true;
}

export default function PageviewBeacon() {
  const location = useLocation();

  useEffect(() => {
    if (!BACKEND_URL) return;
    const path = location.pathname + (location.search || '');
    if (!_shouldTrack(location.pathname)) return;

    const utm = _capturedUtm();
    let abVariant;
    try {
      // Prefer landing-specific key when on that landing; fallback to legacy nis2 key.
      if (location.pathname.startsWith('/landing/ai-act')) {
        abVariant = localStorage.getItem('nis2ai_aiact_ab_variant') || undefined;
      } else if (location.pathname.startsWith('/landing/nis2')) {
        abVariant = localStorage.getItem('nis2ai_ab_variant') || undefined;
      } else {
        abVariant = localStorage.getItem('nis2ai_ab_variant') || localStorage.getItem('nis2ai_aiact_ab_variant') || undefined;
      }
    } catch { /* noop */ }
    const payload = {
      path: path.slice(0, 240),
      session_id: _sessionId(),
      referrer: document.referrer || undefined,
      ab_variant: abVariant,
      ...utm,
    };

    // Use fetch with keepalive so the request survives quick navigations.
    try {
      fetch(`${BACKEND_URL}/api/public/growth/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'omit',
        keepalive: true,
      }).catch(() => { /* silent */ });
    } catch {
      /* silent */
    }
  }, [location.pathname, location.search]);

  return null;
}
